import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import {
  Download,
  Monitor,
  ShieldCheck,
  BookOpen,
  Package,
  CircleCheck,
  Activity,
  GitCompareArrows,
} from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";
import ProductBrandCard from "../ProductInfo/ProductBrandCard";
import ProductVersion from "../ProductInfo/ProductVersion";
import ProductInfoGuide from "../ProductInfo/ProductInfoGuide";

export default function DownloadPage() {
  const { toast } = useToast();
  const [windowsVersion, setWindowsVersion] = useState<string | null>(null);
  const [linuxVersion, setLinuxVersion] = useState<string | null>(null);
  const [macVersion, setMacVersion] = useState<string | null>(null);
  const [versionLoading, setVersionLoading] = useState({
    windows: true,
    linux: true,
    mac: true,
  });

  const downloadLinks = {
    windows: `${NGROK}/api/download`,
    mac: `${NGROK}/api/download/macos`,
    linuxDeb: `${NGROK}/api/download/ubuntu`,
    linuxRpm: `${NGROK}/api/download/rhel`,
  };

  const handleDownload = (
    platform: string,
    packageType: string,
    url: string,
  ) => {
    window.open(url, "_blank", "noopener,noreferrer");
    toast({
      title: "Download Started",
      description: `WhiteSwanSecurity ${packageType} for ${platform} download has been initiated.`,
    });
  };

  const callForWindows = async () => {
    try {
      const response = await axios.get(
        `${NGROK}/api/agent/windows-agent-version`,
      );
      if (response?.data) setWindowsVersion(response.data);
    } catch (error) {
      console.error("Failed to fetch Windows agent version", error);
    } finally {
      setVersionLoading((previous) => ({ ...previous, windows: false }));
    }
  };

  const callForLinux = async () => {
    try {
      const response = await axios.get(
        `${NGROK}/api/agent/linux-agent-version`,
      );
      if (response?.data) setLinuxVersion(response.data);
    } catch (error) {
      console.error("Failed to fetch Linux agent version", error);
    } finally {
      setVersionLoading((previous) => ({ ...previous, linux: false }));
    }
  };

  const callForMac = async () => {
    try {
      const response = await axios.get(`${NGROK}/api/agent/mac-agent-version`);
      if (response?.data) setMacVersion(response.data);
    } catch (error) {
      console.error("Failed to fetch Mac agent version", error);
    } finally {
      setVersionLoading((previous) => ({ ...previous, mac: false }));
    }
  };

  useEffect(() => {
    callForWindows();
    callForLinux();
    callForMac();
  }, []);

  const platforms = [
    {
      id: "windows",
      name: "Windows",
      icon: FaWindows,
      version: windowsVersion,
      loading: versionLoading.windows,
      description: "Install the agent on Windows desktops and servers.",
      compatibility: "Windows 10/11 and Windows Server",
      requirement: "Administrator privileges required",
      packages: [
        {
          type: "MSI",
          url: downloadLinks.windows,
          testId: "download-windows-msi",
        },
      ],
      steps: [
        "Download the MSI installer",
        "Run as Administrator",
        "Follow the setup wizard",
        "The agent starts automatically",
      ],
    },
    {
      id: "mac",
      name: "Mac",
      icon: FaApple,
      version: macVersion,
      loading: versionLoading.mac,
      description: "Install the agent on your macOS devices.",
      compatibility: "macOS 11 and later",
      requirement: "Administrator privileges required",
      packages: [
        { type: "PKG", url: downloadLinks.mac, testId: "download-mac-pkg" },
      ],
      steps: [
        "Download the PKG installer",
        "Double-click the package to open it",
        "Enter your administrator password",
        "Complete the installation",
      ],
    },
    {
      id: "linux",
      name: "Linux",
      icon: FaLinux,
      version: linuxVersion,
      loading: versionLoading.linux,
      description: "Choose the package for your Linux distribution.",
      compatibility: "DEB: Ubuntu and Debian",
      requirement: "RPM: RHEL, CentOS and Fedora",
      packages: [
        {
          type: "DEB",
          url: downloadLinks.linuxDeb,
          testId: "download-linux-deb",
        },
        {
          type: "RPM",
          url: downloadLinks.linuxRpm,
          testId: "download-linux-rpm",
        },
      ],
      steps: [
        "Download the package for your distribution",
        "Install using your package manager",
        "Configure the service settings",
        "Complete the installation",
      ],
    },
  ];

  return (
    <div className="product-info-page downloads-page">
      <ProductBrandCard
        title="Endpoint agents"
        description="Download WhiteSwanSecurity for your devices and get started with installation."
      />
      <div className="product-platform-grid">
        {platforms.map((platform) => (
          <Card key={platform.id} className="product-panel download-platform">
            <CardContent className="download-platform-content">
              <div className="download-platform-heading">
                <span className="product-icon product-platform-icon">
                  <platform.icon />
                </span>
                <ProductVersion
                  version={platform.version}
                  loading={platform.loading}
                  label={`${platform.name} agent version`}
                />
              </div>
              <div className="download-platform-copy">
                <h2>{platform.name}</h2>
                <p>{platform.description}</p>
              </div>
              <div className="download-platform-requirements">
                <p>
                  <Monitor aria-hidden="true" />
                  {platform.compatibility}
                </p>
                <p>
                  {platform.id === "linux" ? (
                    <Package aria-hidden="true" />
                  ) : (
                    <ShieldCheck aria-hidden="true" />
                  )}
                  {platform.requirement}
                </p>
              </div>
              <div className="download-package-actions">
                {platform.packages.map((packageOption) => (
                  <Button
                    key={packageOption.type}
                    onClick={() =>
                      handleDownload(
                        platform.name,
                        packageOption.type,
                        packageOption.url,
                      )
                    }
                    className="product-primary-action"
                    data-testid={packageOption.testId}
                    aria-label={`Download WhiteSwanSecurity ${packageOption.type} for ${platform.name}`}
                  >
                    <Download aria-hidden="true" />
                    Download {packageOption.type}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="product-panel installation-panel">
        <CardContent className="product-panel-content">
          <div className="product-section-heading">
            <span className="product-icon">
              <BookOpen />
            </span>
            <div>
              <h2>Installation guide</h2>
              <p>Follow the steps for your operating system.</p>
            </div>
          </div>
          <div className="installation-grid">
            {platforms.map((platform) => (
              <section key={platform.id} className="installation-platform">
                <h3>
                  <platform.icon aria-hidden="true" />
                  {platform.name}
                </h3>
                <ol>
                  {platform.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        </CardContent>
      </Card>
      <ProductInfoGuide
        title="Verify your installation"
        description="Review your device in Endpoints after completing setup."
        icon={CircleCheck}
        items={[
          {
            title: "Find your device",
            description: "Locate the installed device in the Endpoints list.",
            icon: Monitor,
          },
          {
            title: "Check its status",
            description: "Review the device status and confirm that it is online.",
            icon: Activity,
          },
          {
            title: "Review the agent version",
            description: "Compare its installed agent version with the release shown for its operating system here.",
            icon: GitCompareArrows,
          },
        ]}
      />
    </div>
  );
}
